import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { userAPI, authAPI } from '../../service/api';
import { FiUser, FiCamera, FiMail, FiLock, FiSave } from 'react-icons/fi';
import { resolveUploadUrl } from '../../config/config';
import { toast } from '../../service/swal';
import PageMeta from '../../components/PageMeta';

export default function ProfilePage() {
  const { user, profile, loadUser } = useAuth();
  const avatarInputRef = useRef(null);
  const [form, setForm] = useState({ nom: user?.nom || '', email: user?.email || '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      await userAPI.uploadAvatar(formData);
      await loadUser();
      toast('success', 'Photo de profil mise à jour');
    } catch { toast('error', 'Erreur lors du changement de photo'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim()) return;
    setSaving(true);
    try {
      await userAPI.update(user.id, { nom: form.nom, email: form.email });
      await loadUser();
      toast('success', 'Profil mis à jour');
    } catch (err) { toast('error', err.response?.data?.error || 'Erreur lors de la mise à jour'); }
    finally { setSaving(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdError('');
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPwdError('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPwdError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    setPwdSaving(true);
    try {
      await authAPI.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast('success', 'Mot de passe modifié avec succès');
    } catch (err) { setPwdError(err.response?.data?.error || 'Erreur lors du changement'); }
    finally { setPwdSaving(false); }
  };

  const roleLabels = {
    student: 'Étudiant', owner: 'Propriétaire',
    admin: 'Administrateur', service_acad: 'Service Académique',
  };

  return (
    <div className="page-container py-8">
      <PageMeta title="Mon profil" />
      <div className="mb-8">
        <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: '#0A192F' }}>Mon profil</h1>
        <p className="text-sm mt-1" style={{ color: '#7A828A' }}>Gérez vos informations personnelles</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-root p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiUser className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <h2 className="font-serif font-bold text-base" style={{ color: '#0A192F' }}>Informations générales</h2>
                <p className="text-xs" style={{ color: '#7A828A' }}>Modifiez votre nom et email</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="label-root mb-1 block">Nom complet</label>
                <input className="input-root w-full" value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })} required />
              </div>
              <div>
                <label className="label-root mb-1 block">Adresse email</label>
                <input className="input-root w-full" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              </div>
              <button type="submit" disabled={saving} className="btn-primary-root py-3 px-6 flex items-center gap-2">
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <><FiSave /> Enregistrer</>
                )}
              </button>
            </form>
          </div>

          <div className="card-root p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: '#F4F4F0' }}>
                <FiLock className="w-5 h-5" style={{ color: '#0A192F' }} />
              </div>
              <div>
                <h2 className="font-serif font-bold text-base" style={{ color: '#0A192F' }}>Mot de passe</h2>
                <p className="text-xs" style={{ color: '#7A828A' }}>Changez votre mot de passe</p>
              </div>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="label-root mb-1 block">Mot de passe actuel</label>
                <input className="input-root w-full" type="password" value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  required minLength={6} />
              </div>
              <div>
                <label className="label-root mb-1 block">Nouveau mot de passe</label>
                <input className="input-root w-full" type="password" value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required minLength={6} />
              </div>
              <div>
                <label className="label-root mb-1 block">Confirmer le nouveau mot de passe</label>
                <input className="input-root w-full" type="password" value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required minLength={6} />
              </div>
              {pwdError && <p className="text-sm" style={{ color: '#C62828' }}>{pwdError}</p>}
              <button type="submit" disabled={pwdSaving} className="btn-outline-root py-3 px-6 flex items-center gap-2"
                style={{ borderColor: '#0A192F', color: '#0A192F' }}>
                {pwdSaving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-navy border-t-transparent" />
                ) : (
                  <><FiLock /> Changer le mot de passe</>
                )}
              </button>
            </form>
          </div>
        </div>

        <div>
          <div className="card-root p-6" style={{ position: 'sticky', top: '6rem' }}>
            <div className="flex flex-col items-center text-center mb-6">
              <div className="relative group cursor-pointer mb-3" onClick={() => avatarInputRef.current?.click()}>
                {user?.avatarUrl ? (
                  <img src={resolveUploadUrl(user.avatarUrl)} alt="" className="w-24 h-24 rounded-full object-cover" />
                ) : (
                  <div className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold"
                    style={{ backgroundColor: '#F4F4F0', color: '#0A192F' }}>
                    {user?.nom?.charAt(0) || 'U'}
                  </div>
                )}
                <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition">
                  <FiCamera className="w-6 h-6 text-white" />
                </div>
                <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarChange} />
              </div>
              <h3 className="font-serif font-bold text-lg" style={{ color: '#0A192F' }}>{user?.nom}</h3>
              <span className="text-[11px] font-bold tracking-widest uppercase px-3 py-1 mt-1 inline-block"
                style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}>
                {roleLabels[user?.role] || user?.role}
              </span>
              <p className="text-xs mt-3" style={{ color: '#7A828A' }}>
                <FiMail className="inline w-3 h-3 mr-1" />{user?.email}
              </p>
            </div>

            {profile && (
              <div>
                <hr className="mb-4" style={{ borderColor: '#E5E5E0' }} />
                <p className="label-root mb-3">Informations supplémentaires</p>
                <div className="space-y-2 text-sm">
                  {Object.entries({
                    ...(profile.numeroPasseport && { 'Passeport': profile.numeroPasseport }),
                    ...(profile.nationalite && { 'Nationalité': profile.nationalite }),
                    ...(profile.universiteOrigine && { 'Université': profile.universiteOrigine }),
                    ...(profile.filiere && { 'Filière': profile.filiere }),
                    ...(profile.telephone && { 'Téléphone': profile.telephone }),
                    ...(profile.adresse && { 'Adresse': profile.adresse }),
                  }).map(([label, value]) => (
                    <div key={label} className="flex justify-between py-1" style={{ borderBottom: '1px solid #E5E5E0' }}>
                      <span className="label-root">{label}</span>
                      <span className="font-semibold" style={{ color: '#0A192F' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
